// use-data-sync — fetcht `/api/cache/data` aus dem Hono-Worker, verarbeitet die
// rohe Response mit `processRawCache` und füttert den market-store.
//
// V1-Pendant: src/hooks/useDataSync.js. V3-Vereinfachung: kein Owner-Direct-Fetch
// mehr — der Worker macht die Aggregation in der Cron (Phase 2). Alle User lesen
// aus demselben /api/cache/data Endpoint.
//
// Polling-Intervall: Owner 60s, alle anderen 120s (V1-Verhalten).

import { useCallback, useEffect, useMemo } from 'react';
import { apiFetch } from '@/lib/api';
import { deriveMarketStats, processRawCache, type RawMarketCache } from '@/lib/market-cache';
import {
  selectFullData,
  selectIsSyncing,
  selectLastSyncAt,
  useMarketStore,
} from '@/stores/market-store';
import { selectAccessToken, selectProfile, useAuthStore } from '@/stores/auth-store';
import type { FullData, MarketStats } from '@/types/market';

const OWNER_INTERVAL_MS = 60_000;
const USER_INTERVAL_MS = 120_000;

export interface UseDataSyncReturn {
  fullData: FullData | null;
  marketStats: MarketStats | null;
  isSyncing: boolean;
  lastSyncAt: number | null;
  lastError: string | null;
  /** Manueller Sync (Pull-to-Refresh / Reload-Button). */
  syncNow: () => Promise<FullData | null>;
}

async function performSync(accessToken: string | null): Promise<FullData | null> {
  const raw = await apiFetch<RawMarketCache>('/cache/data', { accessToken });
  return processRawCache(raw);
}

export function useDataSync(): UseDataSyncReturn {
  const fullData = useMarketStore(selectFullData);
  const isSyncing = useMarketStore(selectIsSyncing);
  const lastSyncAt = useMarketStore(selectLastSyncAt);
  const lastError = useMarketStore((s) => s.lastError);
  // Derive marketStats locally with useMemo — avoids infinite loop that selectMarketStats
  // would trigger (deriveMarketStats returns a new object on every call).
  const marketStats = useMemo<MarketStats | null>(() => deriveMarketStats(fullData), [fullData]);

  const profile = useAuthStore(selectProfile);
  const accessToken = useAuthStore(selectAccessToken);
  const role = profile?.role ?? null;

  const syncNow = useCallback<() => Promise<FullData | null>>(async () => {
    const store = useMarketStore.getState();
    if (store.isSyncing) return store.fullData;
    store.setIsSyncing(true);
    try {
      const processed = await performSync(accessToken);
      if (processed) {
        store.markSyncSuccess(processed);
        return processed;
      }
      store.markSyncError('Keine Daten erhalten');
      return null;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync fehlgeschlagen';
      store.markSyncError(msg);
      return null;
    }
  }, [accessToken]);

  useEffect(() => {
    if (!role) return undefined;
    // Initial-Sync sofort
    void syncNow();
    const interval = role === 'owner' ? OWNER_INTERVAL_MS : USER_INTERVAL_MS;
    const id = setInterval(() => {
      void syncNow();
    }, interval);
    return () => clearInterval(id);
  }, [role, syncNow]);

  return { fullData, marketStats, isSyncing, lastSyncAt, lastError, syncNow };
}
